import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiCheck } from "react-icons/fi";
import { MdWarning } from "react-icons/md";
import { HiDocumentDuplicate } from "react-icons/hi";
import { GetInvoiceService } from "../../Services/InvoiceService/InvoiceService";
import Loader from "../../Components/Loader/Loader";
import AlertBanner from "../AlertBanner/AlertBanner";
import "./DuplicateInvoiceCompare.css";
import {
  compareDuplicateInvoiceApi,
  resolveDuplicateApi,
} from "../../Services/DuplicateService/DuplicateApi";
import { DocumentViewer } from "../DocumentViewer/DocumentViewer";
import { parseDocumentUrls } from "../DocumentViewer/utils";

interface InvoiceData {
  invoiceId?: number | string;
  id?: number | string;
  documentUrl?: string | null;
  invoiceDate?: string | null;
  supplier?: string | null;
  documentReference?: string | null;
  type?: string | null;
  category?: string | null;
  currency?: string | null;
  totalAmount?: number | string | null;
  taxAmount?: number | string | null;
  netAmount?: number | string | null;
  [key: string]: any;
}

interface LocationState {
  originalInvoice?: InvoiceData;
  duplicateInvoice?: InvoiceData;
  duplicateInvoiceId?: number | string;
  duplicateIndex?: number;
  totalDuplicates?: number;
  FetchInvoices?: any;
  handleCompare?: any;
}

const DuplicateInvoiceCompare: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const state = (location.state || {}) as LocationState;
  const FetchInvoices = state.FetchInvoices;

  const [originalInvoice, setOriginalInvoice] =
    useState<InvoiceData | null>(
      state.originalInvoice || null,
    );

  const [duplicateLoading, setDuplicateLoading] =
    useState(false);


  const [duplicateInvoice, setDuplicateInvoice] =
    useState<InvoiceData | null>(
      state.duplicateInvoice || null,
    );

  const [loading, setLoading] = useState(true);

  const [currentDuplicateIndex, setCurrentDuplicateIndex] =
    useState(state.duplicateIndex || 0);

  const [totalDuplicates, setTotalDuplicates] = useState(
    state.totalDuplicates || 1,
  );

  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState(false);

  const getduplicateInvoicedata =
    duplicateInvoice?.data?.duplicate;

  const getoriginalInvoicedata =
    originalInvoice?.data?.original;

  const navigation =
    duplicateInvoice?.data?.duplicateGroupNavigation;

  const nextId = navigation?.nextFlagId;
  const previousid = navigation?.previousFlagId;

  const backendCurrentPosition =
    navigation?.currentPosition;

  const backendTotalDuplicates =
    navigation?.totalDuplicates;

  const [actionLoading, setActionLoading] =
    useState(false);

  useEffect(() => {
    const loadInvoices = async () => {
      try {
        setLoading(true);

        let original: InvoiceData | null =
          state.originalInvoice || null;

        let duplicate: InvoiceData | null =
          state.duplicateInvoice || null;

        /*
         * Load Original Invoice
         */
        if (!original) {
          const originalId =
            state.originalInvoice?.invoiceId ||
            state.originalInvoice?.id;

          if (originalId) {
            const result = await GetInvoiceService(
              Number(originalId),
            );

            original = result as InvoiceData | null;
          }
        }

        /*
         * Load Duplicate Invoice
         */
        if (
          !duplicate &&
          state.duplicateInvoiceId
        ) {
          const result = await GetInvoiceService(
            Number(state.duplicateInvoiceId),
          );

          duplicate = result as InvoiceData | null;
        }

        setOriginalInvoice(original || null);
        setDuplicateInvoice(duplicate || null);

        const navigationData =
          duplicate?.data?.duplicateGroupNavigation;

        if (
          navigationData?.currentPosition != null
        ) {
          setCurrentDuplicateIndex(
            Number(
              navigationData.currentPosition,
            ) - 1,
          );
        }

        if (
          navigationData?.totalDuplicates != null
        ) {
          setTotalDuplicates(
            Number(
              navigationData.totalDuplicates,
            ),
          );
        } else if (
          navigationData?.totalCount != null
        ) {
          setTotalDuplicates(
            Number(
              navigationData.totalCount,
            ),
          );
        }
      } catch (error) {
        console.error(
          "Error loading duplicate invoice comparison data:",
          error,
        );
      } finally {
        setLoading(false);
      }
    };

    loadInvoices();
  }, []);

  const formatDate = (value: any) => {
    if (!value) {
      return "";
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString("en-GB");
  };

  const formatAmount = (value: any) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "";
    }

    const numberValue = Number(value);

    if (isNaN(numberValue)) {
      return value;
    }

    return numberValue.toFixed(2);
  };

  const getInvoiceId = (
    invoice: InvoiceData | null,
  ) => {
    return invoice?.invoiceId || invoice?.id || "";
  };

  const getDisplayValue = (value: any) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "—";
    }

    return value;
  };

  const comparisonLabels = [
    "Invoice Date",
    "Supplier",
    "Document Reference",
    "Document Type",
    "Category",
    "Currency",
    "Total Amount",
    "Tax Amount",
    "Net Amount",
  ];

  const normalizeValue = (value: any) => {
    if (
      value === null ||
      value === undefined ||
      String(value).trim() === ""
    ) {
      return "";
    }

    return String(value)
      .trim()
      .toLowerCase();
  };

  const valuesAreDifferent = (
    originalValue: any,
    duplicateValue: any,
  ) => {
    const normalizedOriginal =
      normalizeValue(originalValue);

    const normalizedDuplicate =
      normalizeValue(duplicateValue);

    if (
      normalizedOriginal === "" &&
      normalizedDuplicate === ""
    ) {
      return false;
    }

    return (
      normalizedOriginal !==
      normalizedDuplicate
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  /*
   * YES - SAME INVOICE
   */
  const handleSameInvoice = () => {
    setShowDeleteConfirmation(true);
  };

  /*
   * Confirm permanent deletion
   */
  const handleConfirmDelete = async () => {
    try {
      setActionLoading(true);

      const duplicateId =
        getInvoiceId(duplicateInvoice);

      console.log(
        "Permanently deleting duplicate invoice:",
        duplicateId,
      );

      setShowDeleteConfirmation(false);

      navigate("/inbox");
    } catch (error) {
      console.error(
        "Error deleting duplicate invoice:",
        error,
      );

      alert(
        "Something went wrong. The invoice could not be deleted. Please try again.",
      );
    } finally {
      setActionLoading(false);
    }
  };


const handleDifferentInvoice = async () => {
  try {
    setActionLoading(true);

    const flagId = duplicateInvoice?.data.flagId;

    if (!flagId) {
      console.error("Flag ID is missing");
      return;
    }

    // Resolve current duplicate
    await handleResolveDuplicate(flagId, 2);

    // Refresh invoice list
    if (FetchInvoices) {
      await FetchInvoices();
    }

    const updatedTotalDuplicates = Math.max(
      totalDuplicates - 1,
      0,
    );

    if (nextId != null) {
      console.log(
        "Loading next duplicate:",
        nextId,
      );

      const result =
        await compareDuplicateInvoiceApi(
          Number(nextId),
        );

      if (result) {
        setDuplicateInvoice(result);

        const updatedNavigation =
          result?.data?.duplicateGroupNavigation;

        /*
         * Update current position
         */
        if (
          updatedNavigation?.currentPosition != null
        ) {
          setCurrentDuplicateIndex(
            Number(
              updatedNavigation.currentPosition,
            ) - 1,
          );
        }

        /*
         * Update total count after resolve
         */
        if (
          updatedNavigation?.totalDuplicates != null
        ) {
          setTotalDuplicates(
            Number(
              updatedNavigation.totalDuplicates,
            ),
          );
        } else {
          setTotalDuplicates(
            updatedTotalDuplicates,
          );
        }

      }

      return;
    }

    if (previousid != null) {
      console.log(
        "Loading previous duplicate:",
        previousid,
      );

      const result =
        await compareDuplicateInvoiceApi(
          Number(previousid),
        );

      if (result) {
        setDuplicateInvoice(result);

        const updatedNavigation =
          result?.data?.duplicateGroupNavigation;

        /*
         * Update current position
         */
        if (
          updatedNavigation?.currentPosition != null
        ) {
          setCurrentDuplicateIndex(
            Number(
              updatedNavigation.currentPosition,
            ) - 1,
          );
        }

        if (
          updatedNavigation?.totalDuplicates != null
        ) {
          setTotalDuplicates(
            Number(
              updatedNavigation.totalDuplicates,
            ),
          );
        } else {
          setTotalDuplicates(
            updatedTotalDuplicates,
          );
        }

      }

      return;
    }

    setTotalDuplicates(0);
    navigate("/inbox");

  } catch (error) {
    console.error(
      "Error resolving different invoice:",
      error,
    );
  } finally {
    setActionLoading(false);
  }
};

  const fetchtotal = async () => {
    try {
      const result =
        await compareDuplicateInvoiceApi(
          Number(
            originalInvoice?.data.flagId,
          ),
        );

      return result;
    } catch (error) {
      console.error(
        "Error fetching duplicate invoice:",
        error,
      );
      return null;
    }
  };

  const handlePrevious = async () => {
    if (previousid == null || actionLoading) {
      return;
    }

    try {
      setDuplicateLoading(true);

      const result =
        await compareDuplicateInvoiceApi(
          Number(previousid),
        );

      if (result) {
        setDuplicateInvoice(result);

        const navigation =
          result?.data?.duplicateGroupNavigation;

        if (
          navigation?.currentPosition != null
        ) {
          setCurrentDuplicateIndex(
            Number(
              navigation.currentPosition,
            ) - 1,
          );
        }

        
        if (
          navigation?.totalDuplicates != null
        ) {
          setTotalDuplicates(
            Number(
              navigation.totalDuplicates,
            ),
          );
        } else if (
          navigation?.totalCount != null
        ) {
          setTotalDuplicates(
            Number(
              navigation.totalCount,
            ),
          );
        }
      }
    } catch (error) {
      console.error(
        "Error loading previous duplicate invoice:",
        error,
      );
    } finally {
       setDuplicateLoading(false);
    }
  };

  /*
   * NEXT DUPLICATE
   */
  const handleNext = async () => {
    if (nextId == null || actionLoading) {
      return;
    }

    try {
      setDuplicateLoading(true);

      const result =
        await compareDuplicateInvoiceApi(
          Number(nextId),
        );

      if (result) {
        setDuplicateInvoice(result);

        const navigation =
          result?.data?.duplicateGroupNavigation;


        if (
          navigation?.currentPosition != null
        ) {
          setCurrentDuplicateIndex(
            Number(
              navigation.currentPosition,
            ) - 1,
          );
        }

        if (
          navigation?.totalDuplicates != null
        ) {
          setTotalDuplicates(
            Number(
              navigation.totalDuplicates,
            ),
          );
        } else if (
          navigation?.totalCount != null
        ) {
          setTotalDuplicates(
            Number(
              navigation.totalCount,
            ),
          );
        }
      }
    } catch (error) {
      console.error(
        "Error loading next duplicate invoice:",
        error,
      );
    } finally {
      setDuplicateLoading(false);
    }
  };

  const handleResolveDuplicate = async (
    flagId: number,
    stageid: number,
  ) => {
    try {
      const result =
        await resolveDuplicateApi(
          flagId,
          stageid,
        );

      if (result !== undefined) {
        console.log(
          "Duplicate resolved successfully:",
          result,
        );
      }
    } catch (error) {
      console.error(
        "Error resolving duplicate:",
        error,
      );
    }
  };

  const handleSameInvoiceclick = async (
    stageid: number,
  ) => {
    try {
      setActionLoading(true);

      const flagId = duplicateInvoice?.data?.flagId;


      if (!flagId) {
        console.error("Flag ID is missing");
        return;
      }

      // Delete / resolve current duplicate
      await handleResolveDuplicate(flagId, stageid);

      setShowDeleteConfirmation(false);

      // Refresh inbox invoice list
      if (FetchInvoices) {
        await FetchInvoices();
      }


      if (nextId != null) {
        console.log(
          "Loading next duplicate:",
          nextId,
        );

        const result =
          await compareDuplicateInvoiceApi(
            Number(nextId),
          );

        console.log("result", result);

        if (result) {
          
          setOriginalInvoice(result);
          setDuplicateInvoice(result);

          const updatedNavigation =
            result?.data?.duplicateGroupNavigation;

          if (
            updatedNavigation?.currentPosition != null
          ) {
            setCurrentDuplicateIndex(
              Number(
                updatedNavigation.currentPosition,
              ) - 1,
            );
          }

          if (
            updatedNavigation?.totalDuplicates != null
          ) {
            setTotalDuplicates(
              Number(
                updatedNavigation.totalDuplicates,
              ),
            );
          } else if (
            updatedNavigation?.totalCount != null
          ) {
            setTotalDuplicates(
              Number(
                updatedNavigation.totalCount,
              ),
            );
          }

        }

        return;
      }

    
      if (previousid != null) {
        console.log(
          "Loading previous duplicate:",
          previousid,
        );

        const result =
          await compareDuplicateInvoiceApi(
            Number(previousid),
          );

        if (result) {
         
          setOriginalInvoice(result);
          setDuplicateInvoice(result);

          const updatedNavigation =
            result?.data?.duplicateGroupNavigation;

          if (
            updatedNavigation?.currentPosition != null
          ) {
            setCurrentDuplicateIndex(
              Number(
                updatedNavigation.currentPosition,
              ) - 1,
            );
          }

          if (
            updatedNavigation?.totalDuplicates != null
          ) {
            setTotalDuplicates(
              Number(
                updatedNavigation.totalDuplicates,
              ),
            );
          } else if (
            updatedNavigation?.totalCount != null
          ) {
            setTotalDuplicates(
              Number(
                updatedNavigation.totalCount,
              ),
            );
          }

        }

        return;
      }

      navigate("/inbox");
    } catch (error) {
      console.error(
        "Error resolving same invoice:",
        error,
      );
    } finally {
      setActionLoading(false);
    }
  };

  const comparisonFields =
    comparisonLabels.map(
      (label, index) => ({
        label,
        original:
          getoriginalInvoicedata?.fields?.[index]
            ?.value ?? "",
        duplicate:
          getduplicateInvoicedata?.fields?.[
            index
          ]?.value ?? "",
      }),
    );

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="duplicate-compare-page">

      {/* Top Information Message */}
      <div style={{ marginBottom: "16px" }}>
        <AlertBanner
          type="info"
          text="We found an item that looks similar to one already in your inbox. Review them side by side and tell us if they are the same invoice"
        />
      </div>

      {/* Comparison Area */}
      <div className="duplicate-comparison-container">

        {/* ORIGINAL */}
        <div className="duplicate-invoice-panel">

          <div className="duplicate-panel-title original-title">
            Original
          </div>

          <div className="duplicate-document-container">
            <DocumentViewer compact
              urls={parseDocumentUrls(getoriginalInvoicedata?.documentUrl)}
            />
          </div>

          {/* Original Invoice Details */}
          <div className="invoice-details-table">

            {comparisonFields.map(
              (field, index) => {
                const isDifferent =
                  valuesAreDifferent(
                    field.original,
                    field.duplicate,
                  );

                return (
                  <div
                    className={`invoice-detail-row ${!isDifferent
                        ? "invoice-detail-match-row"
                        : ""
                      }`}
                    key={`original-${index}`}
                  >
                    <div className="invoice-detail-label">
                      {field.label}
                    </div>

                    <div className="invoice-detail-value">
                      {getDisplayValue(field.original)}

                      {!isDifferent && (
                        <span className="field-match-icon">
                          <FiCheck />
                        </span>
                      )}
                    </div>
                  </div>
                );
              },
            )}

          </div>
        </div>

        {/* DUPLICATE */}
        <div className="duplicate-invoice-panel">

          <div className="duplicate-panel-title duplicate-title">
            Duplicate{" "}
            {String(
              currentDuplicateIndex + 1,
            ).padStart(2, "0")}
          </div>

          {duplicateLoading ? (
            <div className="duplicate-panel-loader">
              <Loader />
            </div>
          ) : (
            <>

          <div className="duplicate-document-container">
            <DocumentViewer compact
              urls={parseDocumentUrls(getduplicateInvoicedata?.documentUrl)}
            />
          </div>

          {/* Duplicate Invoice Details */}
          <div className="invoice-details-table">

            {comparisonFields.map(
              (field, index) => {
                const isDifferent =
                  valuesAreDifferent(
                    field.original,
                    field.duplicate,
                  );

                return (
                  <div
                    className={`invoice-detail-row ${!isDifferent
                        ? "duplicate-field-match-row"
                        : ""
                      }`}
                    key={`duplicate-${index}`}
                  >
                    <div className="invoice-detail-label">
                      {field.label}
                    </div>

                    <div
                      className={`invoice-detail-value ${isDifferent
                          ? "duplicate-field-different"
                          : "duplicate-field-match"
                        }`}
                    >
                      <span>
                        {getDisplayValue(
                          field.duplicate,
                        )}
                      </span>

                      {isDifferent ? (
                        <span className="field-warning-icon"
                          title="Fields differ - Review carefully">
                          <MdWarning />
                        </span>
                      ) : (
                        <span className="field-match-icon"
                          title="Fields match ">
                          <FiCheck />
                        </span>
                      )}
                    </div>
                  </div>
                );
              },
            )}

          </div>
            </>
          )}
        </div>
      </div>

      {/* Match Legend */}
      <div className="duplicate-match-legend">

        <span className="match-item">
          <span className="match-icon match">
            <FiCheck />
          </span>

          Fields match
        </span>

        <span className="match-item">
          <span className="match-icon different">
            <MdWarning />
          </span>

          Fields differ - Review carefully
        </span>

      </div>

      {/* Bottom Question */}
      <div className="duplicate-question">
        Is this the same invoice?
      </div>

      {/* Yes / No Buttons */}
      <div className="duplicate-action-buttons">

        <button
          type="button"
          className="duplicate-action-button"
          onClick={handleSameInvoice}
          disabled={actionLoading}
        >
          <span><HiDocumentDuplicate /></span>

          Yes, it's the same invoice
        </button>

        <button
          type="button"
          className="duplicate-action-button"
          onClick={handleDifferentInvoice}
          disabled={actionLoading}
        >
          <span><FiCheck /></span>

          No, it's a different invoice
        </button>

      </div>

      {/* Previous / Next */}
      {totalDuplicates > 1 && (
        <div className="duplicate-navigation">

          <button
            type="button"
            className="duplicate-nav-button"
            disabled={
              previousid == null ||
              actionLoading
            }
            onClick={handlePrevious}
          >
            Previous
          </button>

          <span className="duplicate-count">
            Duplicate{" "}
            {currentDuplicateIndex + 1} of{" "}
            {totalDuplicates}
          </span>

          <button
            type="button"
            className="duplicate-nav-button"
            disabled={
              nextId == null ||
              actionLoading
            }
            onClick={handleNext}
          >
            Next
          </button>

        </div>
      )}

      {/* Back */}
      <button
        type="button"
        className="duplicate-back-button"
        onClick={handleBack}
        disabled={actionLoading}
      >
        Back
      </button>

      {/* Delete Confirmation */}
      {showDeleteConfirmation && (
        <div className="duplicate-confirm-overlay">

          <div className="duplicate-confirm-popup">

            <div className="duplicate-confirm-title">
              Permanently delete duplicate invoice?
            </div>

            <div className="duplicate-confirm-message">
              The duplicate invoice will be permanently
              deleted. The original invoice will be kept.
              This action cannot be undone.
            </div>

            <div className="duplicate-confirm-actions">

              <button
                type="button"
                className="duplicate-confirm-cancel"
                onClick={() =>
                  setShowDeleteConfirmation(
                    false,
                  )
                }
                disabled={actionLoading}
              >
                Cancel
              </button>

              <button
                type="button"
                className="duplicate-confirm-delete"
                onClick={() =>
                  handleSameInvoiceclick(1)
                }
                disabled={actionLoading}
              >
                {actionLoading
                  ? "Deleting..."
                  : "Delete duplicate permanently"}
              </button>

            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default DuplicateInvoiceCompare;